// Founder-level access check.
// Admins run the platform day to day; super admins (the founders) are the only
// ones who can hand out or take away admin access. The email list is a
// fallback so a founder is never locked out before claims sync.

import { useState, useEffect } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '../config/firebase';
import { SUPER_ADMIN_EMAILS } from '../constants/brand';

export function useSuperAdmin() {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      if (cancelled) return;

      if (!user) {
        setIsSuperAdmin(false);
        setLoading(false);
        return;
      }

      const email = user.email?.toLowerCase();
      if (email && SUPER_ADMIN_EMAILS.includes(email)) {
        setIsSuperAdmin(true);
        setLoading(false);
        return;
      }

      try {
        const token = await user.getIdTokenResult();
        if (!cancelled) setIsSuperAdmin(token.claims.superAdmin === true);
      } catch {
        if (!cancelled) setIsSuperAdmin(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return { isSuperAdmin, loading };
}
